/**
 * Vincent B902 (Eric 2026-02-14 SPEC STRICK PROD) — Bandeau des tags
 * suggérés (LLM + TF-IDF hybride) affiché au-dessus du champ tags
 * dans l'Explorateur (modal édition) et sur VideoDetailPage.
 *
 * Fonctionnalités :
 *   - Génère à la demande via bouton ✨ (POST /tags/suggest)
 *   - Accept 1 par 1 ou Tout accepter (bulk)
 *   - Refus 1 par 1 (persistant — `rejected_tags[]` côté back)
 *   - Toutes les mutations sont optimistes + réconciliées avec la
 *     réponse serveur (source de vérité)
 *
 * Réutilise le composant partout où on veut proposer des tags.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_STYLES = {
  llm: { icon: "🤖", bg: "bg-violet-50", border: "border-violet-300", text: "text-violet-900", label: "IA" },
  tfidf: { icon: "📊", bg: "bg-sky-50", border: "border-sky-300", text: "text-sky-900", label: "TF" },
  jargon: { icon: "📚", bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900", label: "Jargon" },
  // Tâche 1269 (Eric 2026-06-29) — Terme du vocabulaire de l'organisation
  // ENTENDU dans la transcription. Coût nul : aucune IA appelée.
  vocabulaire: { icon: "📖", bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-900", label: "Vocabulaire" },
};

function styleForSource(source) {
  const s = (source || "").split("+")[0]; // Premier source si combiné.
  return SOURCE_STYLES[s] || SOURCE_STYLES.tfidf;
}

/**
 * Props:
 *   - videoId (string, required)
 *   - onAccepted(tag) : callback quand un tag passe suggéré → accepté
 *       (permet au parent d'ajouter le tag dans son état local `tags[]`)
 *   - onRejected(tag) : callback quand un tag est refusé
 *   - initialSuggestions (array optionnel) : préchargées par le parent
 *       (évite un GET redondant)
 *   - canEdit (bool) : gate lecture-seule pour non-éditeurs
 *   - lang (string) : "fr" | "en"
 */
export default function TagSuggestionsBar({
  videoId,
  onAccepted,
  onRejected,
  initialSuggestions = null,
  canEdit = true,
  lang = "fr",
}) {
  const [suggestions, setSuggestions] = useState(initialSuggestions || []);
  const [loading, setLoading] = useState(false);
  const [fetchedOnce, setFetchedOnce] = useState(!!initialSuggestions);
  // Tâche 1269 (Eric 2026-06-29) — Termes du VOCABULAIRE de l'organisation
  // retrouvés dans la transcription. Chargés d'office au montage : la
  // détection ne coûte rien (aucune IA), il n'y a donc aucune raison
  // d'attendre un clic. Liste séparée pour survivre à une régénération IA.
  const [vocabMatches, setVocabMatches] = useState([]);

  useEffect(() => {
    if (!videoId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/videos/${videoId}/tags/vocabulary-matches`,
          { withCredentials: true },
        );
        if (!cancelled) setVocabMatches(data?.matches || []);
      } catch { /* silent — fonctionnalité non-bloquante */ }
    })();
    return () => { cancelled = true; };
  }, [videoId]);

  // Charge les suggestions existantes au montage (si le parent n'en a
  // pas fourni). N'en GÉNÈRE PAS de nouvelles — on affiche celles
  // persistées côté back.
  useEffect(() => {
    if (fetchedOnce || !videoId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/videos/${videoId}/tags/suggestions`,
          { withCredentials: true },
        );
        if (cancelled) return;
        setSuggestions(data?.suggested_tags || []);
      } catch { /* silent — feature non-bloquante */ }
      finally { if (!cancelled) setFetchedOnce(true); }
    })();
    return () => { cancelled = true; };
  }, [videoId, fetchedOnce]);

  const regenerate = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/tags/suggest`,
        {},
        { withCredentials: true },
      );
      setSuggestions(data?.suggested_tags || []);
      const n = data?.count || 0;
      if (n > 0) {
        toast.success(lang === "fr" ? `${n} tag(s) suggéré(s)` : `${n} tag(s) suggested`);
      } else {
        toast.info(lang === "fr" ? "Aucun nouveau tag suggéré." : "No new tag suggested.");
      }
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") +
        (err?.response?.data?.detail || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  // Vincent B964 (Eric 2026-02-14 SPEC STRICK PROD) — La boite n'apparait
  // QUE si (a) des suggestions existent deja OU (b) l'utilisateur vient
  // de cliquer sur "Suggerer tags" dans le header (declenche loading OR
  // openedManually). Auparavant la boite occupait de l'espace en
  // permanence pour les editeurs meme sans suggestion.
  const [openedManually, setOpenedManually] = useState(false);

  // Vincent B950 (Eric 2026-02-14 SPEC STRICK PROD) — Le chip
  // « ✨ Suggérer tags » du header (entre Étiquettes et Chemin Dropbox)
  // déclenche ce composant via un CustomEvent. Découple les 2 composants
  // sans prop-drilling à travers VideoDetailPage → VideoDetailHeader.
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.videoId && e.detail.videoId !== videoId) return;
      setOpenedManually(true);
      regenerate();
    };
    window.addEventListener("knoria:suggest-tags", handler);
    return () => window.removeEventListener("knoria:suggest-tags", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, loading]);

  const acceptOne = async (tag) => {
    // Optimistic remove.
    setSuggestions((prev) => prev.filter((s) => s.tag !== tag));
    setVocabMatches((prev) => prev.filter((s) => s.tag !== tag));
    try {
      await axios.post(
        `${API}/api/videos/${videoId}/tags/accept`,
        { tag },
        { withCredentials: true },
      );
      if (onAccepted) onAccepted(tag);
    } catch (err) {
      // Réverse en cas d'erreur — remet le tag.
      setSuggestions((prev) => [{ tag, source: "llm", score: 0.5 }, ...prev]);
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") +
        (err?.response?.data?.detail || err.message),
      );
    }
  };

  const rejectOne = async (tag) => {
    setSuggestions((prev) => prev.filter((s) => s.tag !== tag));
    setVocabMatches((prev) => prev.filter((s) => s.tag !== tag));
    try {
      await axios.post(
        `${API}/api/videos/${videoId}/tags/reject`,
        { tag },
        { withCredentials: true },
      );
      if (onRejected) onRejected(tag);
    } catch (err) {
      setSuggestions((prev) => [{ tag, source: "llm", score: 0.5 }, ...prev]);
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") +
        (err?.response?.data?.detail || err.message),
      );
    }
  };

  const acceptAll = async () => {
    const tags = [...suggestions, ...vocabMatches].map((s) => s.tag);
    if (tags.length === 0) return;
    setSuggestions([]);
    setVocabMatches([]);
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/tags/accept`,
        { tags },
        { withCredentials: true },
      );
      const added = data?.added || tags;
      added.forEach((t) => { if (onAccepted) onAccepted(t); });
      toast.success(
        lang === "fr" ? `${added.length} tag(s) acceptés` : `${added.length} tag(s) accepted`,
      );
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") +
        (err?.response?.data?.detail || err.message),
      );
    }
  };

  const rejectAll = async () => {
    const tags = [...suggestions, ...vocabMatches].map((s) => s.tag);
    if (tags.length === 0) return;
    setSuggestions([]);
    setVocabMatches([]);
    try {
      await axios.post(
        `${API}/api/videos/${videoId}/tags/reject`,
        { tags },
        { withCredentials: true },
      );
      tags.forEach((t) => { if (onRejected) onRejected(t); });
      toast.success(
        lang === "fr" ? `${tags.length} tag(s) refusés` : `${tags.length} tag(s) rejected`,
      );
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") +
        (err?.response?.data?.detail || err.message),
      );
    }
  };

  // Vincent B964 (Eric 2026-02-14 SPEC STRICK PROD) — Boite masquee sauf
  // si suggestions existent OU si l'utilisateur vient de cliquer
  // « Suggerer tags » (loading OU openedManually). Ne plus occuper
  // d'espace visuel par defaut.
  if (suggestions.length === 0 && vocabMatches.length === 0 && !loading && !openedManually) return null;

  const renderChip = (s) => {
    const style = styleForSource(s.source);
    const titleParts = [style.label];
    if (s.occurrences) titleParts.push(`${s.occurrences} occurrence(s)`);
    else titleParts.push(`${Math.round((s.score || 0) * 100)}%`);
    if (s.reason) titleParts.push(s.reason);
    if (s.excerpt) titleParts.push(s.excerpt);
    return (
      <span
        key={`${s.source}-${s.tag}`}
        className={`inline-flex items-center gap-1 text-[11px] rounded-full border px-2 py-0.5 ${style.bg} ${style.border} ${style.text}`}
        data-testid={`tag-suggestion-chip-${s.tag}`}
        data-source={s.source}
        title={titleParts.join(" · ")}
      >
        {canEdit && (
          <>
            <button
              type="button"
              onClick={() => acceptOne(s.tag)}
              className="rounded-full bg-teal-600 text-white w-4 h-4 inline-flex items-center justify-center text-[10px] font-bold hover:bg-teal-700"
              data-testid={`tag-suggestion-accept-${s.tag}`}
              aria-label={lang === "fr" ? `Accepter ${s.tag}` : `Accept ${s.tag}`}
              title={lang === "fr" ? `Ajouter « ${s.tag} » aux tags` : `Add « ${s.tag} » to tags`}
            >
              ✓
            </button>
            <button
              type="button"
              onClick={() => rejectOne(s.tag)}
              className="rounded-full bg-white border border-zinc-300 text-zinc-600 w-4 h-4 inline-flex items-center justify-center text-[10px] font-bold hover:bg-zinc-100"
              data-testid={`tag-suggestion-reject-${s.tag}`}
              aria-label={lang === "fr" ? `Refuser ${s.tag}` : `Reject ${s.tag}`}
              title={lang === "fr" ? `Refuser « ${s.tag} » (ne plus proposer)` : `Reject « ${s.tag} » (persistent)`}
            >
              ✕
            </button>
          </>
        )}
        {/* B1114 (Eric 2026-09-08) — Ordre imposé de gauche à droite :
            check, croix, émoji de provenance, puis le tag. La décision
            se prend d'un geste sur une liste : les deux boutons sont
            donc alignés en tête de chaque puce. */}
        <span aria-hidden="true">{style.icon}</span>
        <span className="font-medium">{s.tag}</span>
        {s.occurrences > 1 && (
          <span className="text-[10px] opacity-70">×{s.occurrences}</span>
        )}
      </span>
    );
  };

  return (
    <div
      className="mb-2 rounded-md border border-zinc-200 bg-zinc-50 p-2"
      data-testid="tag-suggestions-bar"
      data-suggestions-count={suggestions.length}
      data-vocab-count={vocabMatches.length}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <p className="text-[11px] font-semibold text-zinc-700">
          ✨ {lang === "fr" ? "Tags suggérés" : "Suggested tags"}
          {suggestions.length + vocabMatches.length > 0 && (
            <span className="ml-1 text-zinc-500 font-normal">({suggestions.length + vocabMatches.length})</span>
          )}
        </p>
        {canEdit && suggestions.length + vocabMatches.length > 0 && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={acceptAll}
              className="text-[10px] font-semibold px-2 py-0.5 rounded bg-teal-600 text-white hover:bg-teal-700"
              data-testid="tag-suggestions-accept-all-btn"
              title={lang === "fr" ? "Accepter tous les tags suggérés" : "Accept all suggested tags"}
            >
              ✓ {lang === "fr" ? "Tout accepter" : "Accept all"}
            </button>
            <button
              type="button"
              onClick={rejectAll}
              className="text-[10px] font-semibold px-2 py-0.5 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-100"
              data-testid="tag-suggestions-reject-all-btn"
              title={lang === "fr" ? "Refuser tous les tags suggérés (persistant)" : "Reject all suggested tags (persistent)"}
            >
              ✕ {lang === "fr" ? "Tout refuser" : "Reject all"}
            </button>
          </div>
        )}
        {/* Vincent B964 (Eric 2026-02-14 SPEC STRICK PROD) — Bouton
            « Suggerer tags » interne retire (redondant avec la chip du
            header entre « Étiquettes » et « Chemin Dropbox »). */}
      </div>
      {/* Tâche 1269 — Le vocabulaire de l'organisation ENTENDU dans la
          transcription. Section distincte : sa provenance est vérifiable
          (nombre d'occurrences + extrait), contrairement à une suggestion IA. */}
      {vocabMatches.length > 0 && (
        <div className="mb-2" data-testid="tag-vocabulary-matches">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-emerald-800 mb-1">
            📖 {lang === "fr"
              ? "Vocabulaire détecté dans la transcription"
              : "Vocabulary detected in the transcript"}
            <span className="ml-1 font-normal text-emerald-700 normal-case">({vocabMatches.length})</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {vocabMatches.map(renderChip)}
          </div>
        </div>
      )}
      {suggestions.length === 0 ? (
        vocabMatches.length === 0 && (
          <p className="text-[11px] text-zinc-500 italic">
            {loading
              ? (lang === "fr" ? "⏳ Génération en cours…" : "⏳ Generating…")
              : canEdit
                ? (lang === "fr"
                    ? "Aucune suggestion trouvée. Réessaie avec « ✨ Suggérer tags » sous le titre."
                    : "No suggestions found. Try « ✨ Suggest tags » under the title.")
                : (lang === "fr" ? "Aucun tag suggéré." : "No suggested tags.")}
          </p>
        )
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map(renderChip)}
        </div>
      )}
    </div>
  );
}
